import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { CampaignContactRow } from '#/entities/campaign'
import { SHIPMENT_STAGES } from '#/entities/shipment'
import { getListShipmentsApiV1ShipmentsGetQueryOptions } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'
import { useOpenShipmentPreview } from '#/widgets/shipment-sidebar'

/** How many search hits to scan for the exact code — a code search is
 *  prefix-ish, so `CG2159` can also match `CG21591`. */
const CODE_SEARCH_SIZE = 5

/** `#CG215921` → `CG215921`; the row's code carries the hash, the list's
 *  does not. */
function bareCode(code: string): string {
  return code.trim().replace(/^#/, '').toUpperCase()
}

/** A row's quote code that names a shipment, as against the em-dash the
 *  service sends when there is none. */
export function hasQuoteCode(row: CampaignContactRow): boolean {
  return /[A-Z0-9]/i.test(row.quote.code)
}

/**
 * The quote code on a Contact row opens its shipment in the mini sidebar.
 *
 * A trigger enrolment carries the quote's `shipment_id`; a manual one usually
 * does not (docs/project/campaign.md), though the service still names the
 * quote by code. That code is looked up on the same `GET /shipments?q=` the
 * global search uses, and only an exact match opens — at the stage the
 * shipment is actually in. With an id, a campaign chases quotes, so that is
 * the stage it opens at and the opener corrects itself from the cached record.
 */
export function useOpenCampaignQuote() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const openPreview = useOpenShipmentPreview()

  const openByCode = useCallback(
    async (code: string) => {
      try {
        const response = await queryClient.fetchQuery(
          getListShipmentsApiV1ShipmentsGetQueryOptions({
            q: code,
            size: CODE_SEARCH_SIZE,
          }),
        )
        const match = response.data.items.find(
          (item) => bareCode(item.code) === code,
        )
        if (match == null) {
          toast.error(t('campaigns.contact.quoteNotFound', { code }))
          return
        }
        openPreview(match.stage, match.id, match.status)
      } catch (error) {
        toast.error(
          getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
        )
      }
    },
    [queryClient, openPreview, t],
  )

  return useCallback(
    (row: CampaignContactRow) => {
      if (row.shipmentId != null) {
        openPreview(SHIPMENT_STAGES.QUOTE, row.shipmentId)
        return
      }
      if (!hasQuoteCode(row)) return
      void openByCode(bareCode(row.quote.code))
    },
    [openPreview, openByCode],
  )
}
